import { type FC, useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { DollarSign, X } from 'lucide-react'
import type { FiatCurrency } from '@/types/exchange-rate'

interface CurrencyItem {
    code: string
    name: string
    symbol: string
    enabled: boolean
}

interface AddCurrencyModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    availableCurrencies: FiatCurrency[]
    onSave?: (enabledCurrencies: string[]) => void
    isSubmitting?: boolean
}

export const AddCurrencyModal: FC<AddCurrencyModalProps> = ({
    open,
    onOpenChange,
    availableCurrencies,
    onSave,
    isSubmitting = false,
}) => {
    // Derive initial currencies from available currencies
    const initialCurrencies = useMemo(
        () =>
            availableCurrencies.map(currency => ({
                code: currency.code,
                name: currency.name,
                symbol: currency.symbol,
                enabled: true, // All fetched currencies are considered enabled
            })),
        [availableCurrencies]
    )

    const [currencies, setCurrencies] = useState<CurrencyItem[]>([])

    // Sync currencies when initialCurrencies change
    useEffect(() => {
        if (initialCurrencies.length > 0) {
            setTimeout(() => setCurrencies(initialCurrencies), 0)
        }
    }, [initialCurrencies])

    const handleToggle = (code: string) => {
        setCurrencies(prev =>
            prev.map(currency =>
                currency.code === code
                    ? { ...currency, enabled: !currency.enabled }
                    : currency
            )
        )
    }

    const handleSave = () => {
        if (isSubmitting) return
        const enabledCurrencies = currencies
            .filter(c => c.enabled)
            .map(c => c.code)
        onSave?.(enabledCurrencies)
    }

    const getFlagIcon = (code: string) => {
        const countryCode =
            code === 'ngn'
                ? 'ng'
                : code === 'usd'
                  ? 'us'
                  : code === 'cad'
                    ? 'ca'
                    : code.slice(0, 2)
        return (
            <div className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full">
                <span className="text-xl leading-none">
                    {countryCode === 'ng'
                        ? '🇳🇬'
                        : countryCode === 'us'
                          ? '🇺🇸'
                          : countryCode === 'ca'
                            ? '🇨🇦'
                            : '🌐'}
                </span>
            </div>
        )
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                showCloseButton={false}
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100%-1rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden border-0 bg-transparent p-0 shadow-2xl data-[state=closed]:fade-out data-[state=open]:fade-in sm:max-w-md sm:rounded-2xl"
            >
                <div className="flex flex-col overflow-hidden rounded-2xl bg-white">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3 border-b px-6 py-4">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E5F5F9]">
                                <DollarSign className="h-5 w-5 text-[#0F172A]" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-[#0F172A]">
                                    Add new currencies
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    Add to the list of currencies you have on
                                    your system
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onOpenChange(false)}
                            className="h-8 w-8 rounded-full"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Currency List */}
                    <div className="max-h-[400px] space-y-3 overflow-y-auto px-6 py-5">
                        {currencies.map(currency => (
                            <div
                                key={currency.code}
                                className="flex items-center justify-between rounded-xl border px-4 py-3"
                            >
                                <div className="flex items-center gap-3">
                                    {getFlagIcon(currency.code)}
                                    <span className="text-sm font-medium text-[#0F172A]">
                                        {currency.name}
                                    </span>
                                </div>
                                <Switch
                                    checked={currency.enabled}
                                    onCheckedChange={() =>
                                        handleToggle(currency.code)
                                    }
                                    disabled={isSubmitting}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Footer */}
                    <div className="flex flex-col gap-3 border-t px-6 py-4 sm:flex-row sm:justify-end sm:gap-4">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isSubmitting}
                            className="h-12 flex-1 rounded-full sm:flex-none sm:min-w-[140px]"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={isSubmitting}
                            className="h-12 flex-1 rounded-full bg-[#17A2B8] text-white hover:bg-[#1493a7] sm:flex-none sm:min-w-[140px]"
                        >
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
